import { useMemo } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';
import * as styles from './styles/styles.module.scss';
import CommunityStat from './CommunityStat';
import useFormatMessage from '@alltrails/shared/hooks/useFormatMessage';
import { MapStatsUtil } from '../../../utils/map_stats_util';
import useDisplayMetric from '../../../hooks/useDisplayMetric';

const COMMUNTIY_STATS_STRINGS = defineMessages({
  CURATED_TRAILS: {
    defaultMessage: 'curated trails'
  },
  DISCOVER_UNEXPECTED_GEMS: {
    defaultMessage: 'Discover unexpected gems, even in your own backyard.'
  },
  FELLOW_EXPLORERS: {
    defaultMessage: 'fellow explorers'
  },
  SHARE_YOUR_ADVENTURES: {
    defaultMessage: 'Share your adventures and learn from our global community.'
  },
  LOGGED_MILES: {
    defaultMessage: 'logged miles'
  },
  LOGGED_KILOMETERS: { defaultMessage: 'logged kilometers' },
  NAVIGATE_YOUR_WAY: { defaultMessage: 'Navigate your way and keep a record of all your travels.' }
});

/**
 * CommunityStats takes numbers provided from the parent and parses
 * them into translatable text that can be dynamic
 *
 *
 * @param {number} curatedTrails number of curated trails in thousands
 * @param {number} fellowExplorers Number of users in millions
 * @param {number} loggedDistanceMeters Number of logged usage of the app in meters
 */

type Props = {
  curatedTrails: number;
  fellowExplorers: number;
  loggedDistanceMeters: number;
};

const CommunityStats = ({ curatedTrails, fellowExplorers, loggedDistanceMeters }: Props): JSX.Element => {
  const {
    formattedDefaultMessages: {
      LOGGED_KILOMETERS,
      LOGGED_MILES,
      CURATED_TRAILS,
      DISCOVER_UNEXPECTED_GEMS,
      FELLOW_EXPLORERS,
      SHARE_YOUR_ADVENTURES,
      NAVIGATE_YOUR_WAY
    }
  } = useFormatMessage(COMMUNTIY_STATS_STRINGS);
  const displayMetric = useDisplayMetric();
  const [totalDistance, loggedDistanceMeasurement] = useMemo(() => {
    return displayMetric
      ? [Math.round(loggedDistanceMeters / 1000), LOGGED_KILOMETERS]
      : [MapStatsUtil.metersToMiles(loggedDistanceMeters, 0), LOGGED_MILES];
  }, [displayMetric, loggedDistanceMeters]);

  return (
    <div className={styles.container}>
      <CommunityStat
        className={styles.communityStat}
        title={<FormattedMessage defaultMessage={'{curatedTrails}k+'} values={{ curatedTrails }} />}
        subtitle={CURATED_TRAILS}
        text={DISCOVER_UNEXPECTED_GEMS}
      />
      <CommunityStat
        className={styles.communityStat}
        title={<FormattedMessage defaultMessage={'{fellowExplorers} mil'} values={{ fellowExplorers }} />}
        subtitle={FELLOW_EXPLORERS}
        text={SHARE_YOUR_ADVENTURES}
      />
      <CommunityStat
        className={styles.communityStat}
        title={<FormattedMessage defaultMessage={'{totalDistance}+ mil'} values={{ totalDistance }} />}
        subtitle={loggedDistanceMeasurement}
        text={NAVIGATE_YOUR_WAY}
      />
    </div>
  );
};

export default CommunityStats;
